/** Heatmap — rows × cols grid of colored cells. Ported from the design's heatmap
 *  (capability matrix, parity ledger). `getCell` returns the fill/fg/stroke per cell
 *  and `format` renders the cell text — the caller owns the color scale. */
import { useTokens } from './useTokens'
import { useChartWidth } from './useChartWidth'

export interface HeatCell {
  bg: string
  fg: string
  stroke?: string | null
  [key: string]: unknown
}
interface Row {
  label: string
  strong?: boolean
}
interface Props {
  rows: Row[]
  cols: string[]
  getCell: (row: Row, col: string) => HeatCell
  format: (cell: HeatCell) => string
}

const CH = 34 // row height
const LW = 110 // row-label gutter
const HH = 30 // column-header band

function Cells({ r, ri, cols, cw, getCell, format }: {
  r: Row; ri: number; cols: string[]; cw: number
  getCell: Props['getCell']; format: Props['format']
}) {
  return (
    <>
      {cols.map((c, ci) => {
        const cell = getCell(r, c)
        const x = LW + cw * ci + 3
        const y = HH + CH * ri + 3
        return (
          <g key={`x${ri}-${ci}`}>
            <rect x={x} y={y} width={cw - 6} height={CH - 6} rx={5} fill={cell.bg}
              stroke={cell.stroke || 'transparent'} strokeWidth={cell.stroke ? 1.5 : 0} />
            <text x={x + (cw - 6) / 2} y={y + (CH - 6) / 2 + 4} textAnchor="middle" fill={cell.fg}
              fontSize={11} fontWeight={600} fontFamily="JetBrains Mono">
              {format(cell)}
            </text>
          </g>
        )
      })}
    </>
  )
}

export default function Heatmap({ rows, cols, getCell, format }: Props) {
  const t = useTokens()
  const [box, W] = useChartWidth(600)
  // Columns take the measured width rather than a fixed 92px scaled to fit — that
  // letterboxed the grid into the middle of the panel. Capped, because a five-column
  // ledger stretched across a wide screen is a row of near-empty pills; whatever is
  // left over is centred air, not a distended cell.
  const cw = Math.min(150, Math.max(72, (W - LW) / Math.max(cols.length, 1)))
  const totalW = LW + cw * cols.length
  const totalH = HH + CH * rows.length
  return (
    <div ref={box} className="w-full min-w-0">
      <svg viewBox={`0 0 ${totalW} ${totalH}`}
        style={{ width: '100%', maxWidth: totalW, height: totalH, display: 'block', margin: '0 auto' }}>
        {cols.map((c, ci) => (
          <text key={`c${ci}`} x={LW + cw * ci + cw / 2} y={20} textAnchor="middle" fill={t.mut} fontSize={10.5} fontFamily="JetBrains Mono">
            {c}
          </text>
        ))}
        {rows.map((r, ri) => (
          <g key={`r${ri}`}>
            <text x={LW - 10} y={HH + CH * ri + CH / 2 + 4} textAnchor="end" fill={t.mut} fontSize={11.5} fontWeight={r.strong ? 700 : 400}>
              {r.label}
            </text>
            <Cells r={r} ri={ri} cols={cols} cw={cw} getCell={getCell} format={format} />
          </g>
        ))}
      </svg>
    </div>
  )
}
